import { lazy } from 'react';

// lazy loading wrapper
import Loadable from '../components/Loadable';

// layouts
import DashboardLayout from '../layout/Dashboard';
import AuthLayout from '../layout/Auth';
import RouteError from '@common/error-boundary/RouteError';
// protected route
import ProtectedRoute from '../auth/ProtectedRoute';
import PermissionRoute from '../auth/PermissionRoute';
import BusinessTypeRoute from '../auth/BusinessTypeRoute';
import Calendar from '../views/calendar/Calendar';
import ViewAllNotifications from '../views/notification/ViewAllNotifications';
import PublicRoute from '../auth/PublicRoute';
// dashboard page
const DashboardHome = Loadable(lazy(() => import('../views/dashboard')));

// auth pages
const LoginPage = Loadable(lazy(() => import('../views/auth/login/Login')));
const RegisterPage = Loadable(lazy(() => import('../views/auth/register/Register')));
const ForgotPasswordPage = Loadable(lazy(() => import('../views/auth/forgotPassword/Forgot')));
const HandoffPage = Loadable(lazy(() => import('../views/auth/HandoffPage')));
const RolesPermissions = Loadable(lazy(() => import('../views/roles/RolesPermissions')));
const Settings = Loadable(lazy(() => import('../views/settings/Settings')));
const Restaurant = Loadable(lazy(() => import('../views/restaurant/Restaurant')));
const AddRestaurant = Loadable(lazy(() => import('../views/restaurant/components/AddRestaurant')));
const ViewRestaurant = Loadable(lazy(() => import('../views/restaurant/components/ViewRestaurantPage')));
const User = Loadable(lazy(() => import('../views/user/User')));
const AddUser = Loadable(lazy(() => import('../views/user/components/AddUser')));
const ViewUser = Loadable(lazy(() => import('../views/user/components/ViewUsersPage')));
const PreferenceAmenities = Loadable(lazy(() => import('../views/preferences/PreferenceAmenities')));
const CustomSetup = Loadable(lazy(() => import('../views/customsetup/CustomSetup')));
const AddServicePeriod = Loadable(lazy(() => import('../views/customsetup/components/Service/AddServicePeriod')));
const BookingManagement = Loadable(lazy(() => import('../views/bookingmanagement/BookingManagement')));
const AddNewBooking = Loadable(lazy(() => import('../views/bookingmanagement/components/AddBooking/AddNewBooking')));
const PublicBooking = Loadable(lazy(() => import('../views/public/Booking/PublicBookingWrapper')));
const HomeRedirect = Loadable(lazy(() => import('../views/landing/HomeRedirect')));
const GiftCard = Loadable(lazy(() => import('../views/GiftCard/GiftTicketContainer')));
// const AddGiftCard = Loadable(lazy(() => import('../views/GiftCard/component/GiftCard/AddGiftCard')));
const AddGiftCard = Loadable(lazy(() => import('../views/GiftCard/component/GiftCard/pages/AddGiftCardPage')));
const EditGiftCard = Loadable(lazy(() => import('../views/GiftCard/component/GiftCard/pages/EditGiftCardPage')));
const PublicGiftCardView = Loadable(lazy(() => import('../views/public/GiftCards/PublicGiftCardView')));
const ScanGiftCard = Loadable(lazy(() => import('../views/GiftCard/component/ScanGiftCard/ScanGiftCard')));
const PublicGiftCardUserContainer = Loadable(
  lazy(() => import('../views/public/GiftCards/PublicGiftCardUserContainer/PublicGiftCardUserContainer'))
);
const NotFoundPage = Loadable(lazy(() => import('../views/notfound/NotFound')));
const MenusPage = Loadable(lazy(() => import('../views/menus/Menus')));
const AddMenuPage = Loadable(lazy(() => import('../views/menus/pages/AddMenuPage/AddMenuPage')));
const Loyalty = Loadable(lazy(() => import('../views/loyalty/Loyalty')));
const StoreRoom = Loadable(lazy(() => import('../views/storeRoom/StoreRoom')));
const PosSetupPage = Loadable(lazy(() => import('../views/pos/PosSetupPage')));
const PublicPosInvoiceView = Loadable(lazy(() => import('../views/public/PosInvoice/PublicPosInvoiceView')));
const EditMenuPage = Loadable(lazy(() => import('../views/menus/pages/EditMenuPage/EditMenuPage')));

// Ticket booking (public)
const PublicTicketWrapper = Loadable(lazy(() => import('../views/public/Tickets/PublicTicketWrapper')));
const PublicTicketEventDetail = Loadable(lazy(() => import('../views/public/Tickets/EventDetailPage/EventDetailPage')));
const BookingSuccess = Loadable(lazy(() => import('../views/public/Tickets/BookingSuccess')));
const BookingCancel = Loadable(lazy(() => import('../views/public/Tickets/BookingCancel')));
const BookingConfirmationPage = Loadable(lazy(() => import('../views/public/Tickets/BookingConfirmationPage/BookingConfirmationPage')));

// Voucher pages
const VoucherContainer = Loadable(lazy(() => import('../views/Voucher/VoucherContainer')));
const VoucherBatchDetail = Loadable(lazy(() => import('../views/Voucher/components/VoucherBatch/VoucherBatchDetail')));
const PublicVoucherWrapper = Loadable(lazy(() => import('../views/public/Vouchers/PublicVoucherWrapper')));
const PublicVoucherClaimWrapper = Loadable(lazy(() => import('../views/public/Vouchers/PublicVoucherClaimWrapper')));
const PublicVoucherPassPage = Loadable(lazy(() => import('../views/public/Vouchers/PublicVoucherPassPage')));

// Legal pages
const LegalPage = Loadable(lazy(() => import('../views/public/Legal/LegalPage')));

// Payment result
const PaymentResult = Loadable(lazy(() => import('../views/payment/PaymentResult')));

// Ticket & Event admin pages
const TicketEventContainer = Loadable(lazy(() => import('../views/ticketsAndEvents/TicketEventContainer')));
const AddEvent = Loadable(lazy(() => import('../views/ticketsAndEvents/pages/AddEventPage/AddEventPage')));
const EditEvent = Loadable(lazy(() => import('../views/ticketsAndEvents/pages/EditEventPage/EditEventPage')));
const TicketBookingPage = Loadable(lazy(() => import('../views/ticketsAndEvents/pages/TicketBookingPage/TicketBookingPage')));
const TicketScanPage = Loadable(lazy(() => import('../views/ticketsAndEvents/pages/TicketScanPage/TicketScanPage')));

const PagesRoutes = {
  path: '/',
  errorElement: <RouteError />,
  children: [
    {
      path: '/',
      element: <HomeRedirect />
    },

    {
      element: <AuthLayout />,
      children: [
        {
          path: 'login',
          element: (
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          )
        },
        {
          path: 'signup',
          element: (
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          )
        },
        {
          path: 'forgot-password',
          element: (
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          )
        },
        { path: '*', element: <NotFoundPage /> }
      ]
    },

    {
      element: (
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      ),
      errorElement: <RouteError />,
      children: [
        {
          path: 'dashboard',
          element: (
            <PermissionRoute perm="dashboard_access">
              <DashboardHome />
            </PermissionRoute>
          )
        },
        {
          path: 'roles-permissions',
          element: (
            <PermissionRoute perm="role_permission_access">
              <RolesPermissions />
            </PermissionRoute>
          )
        },
        { path: 'settings', element: <Settings /> },
        {
          path: 'restaurant',
          element: (
            <PermissionRoute perm="restaurant_listing">
              <Restaurant />
            </PermissionRoute>
          )
        },
        { path: 'restaurant/add', element: <AddRestaurant /> },
        { path: 'restaurant/view/:id', element: <ViewRestaurant /> },

        {
          path: 'users',
          element: (
            <PermissionRoute perm="user_listing">
              <User />
            </PermissionRoute>
          )
        },
        { path: 'users/add', element: <AddUser /> },
        { path: 'users/view/:id', element: <ViewUser /> },

        {
          path: 'facilities-preference',
          element: (
            <PermissionRoute perm="preference_listing">
              <PreferenceAmenities />
            </PermissionRoute>
          )
        },
        {
          path: 'custom-setup',
          element: (
            <PermissionRoute perm="custom_setup_listing">
              <CustomSetup />
            </PermissionRoute>
          )
        },
        {
          path: 'custom-setup/serviceperiod/add',
          element: (
            <PermissionRoute perm="custom_setup_listing">
              <AddServicePeriod />
            </PermissionRoute>
          )
        },

        {
          path: 'booking-management',
          element: (
            <PermissionRoute perm="booking_listing">
              <BookingManagement />
            </PermissionRoute>
          )
        },
        {
          path: 'booking-management/new-booking',
          element: (
            <PermissionRoute perm="booking_add">
              <AddNewBooking />
            </PermissionRoute>
          )
        },

        { path: 'calendar', element: <Calendar /> },
        { path: 'notifications', element: <ViewAllNotifications /> },
        {
          path: 'gift_card',
          element: (
            <PermissionRoute perm="giftcard_listing">
              <GiftCard />{' '}
            </PermissionRoute>
          )
        },
        {
          path: 'gift_card/add',
          element: (
            <PermissionRoute perm="giftcard_add">
              <AddGiftCard />
            </PermissionRoute>
          )
        },
        {
          path: 'gift_card/edit/:id',
          element: (
            <PermissionRoute perm="giftcard_edit">
              <EditGiftCard />
            </PermissionRoute>
          )
        },
        {
          path: 'gift_card/scan',
          element: <ScanGiftCard />
        },

        // ── Vouchers ──────────────────────────────────────────────────────
        {
          path: 'vouchers',
          element: (
            <PermissionRoute perm={['voucher_listing', 'public_pass_listing']}>
              <VoucherContainer />
            </PermissionRoute>
          )
        },
        {
          path: 'vouchers/batch/:id',
          element: (
            <PermissionRoute perm={['voucher_view', 'public_pass_view']}>
              <VoucherBatchDetail />
            </PermissionRoute>
          )
        },
// ── Ticket & Event management ──────────────────────────────────────
        {
          path: 'ticket',
          element: <TicketEventContainer />
        },
        {
          path: 'event/add',
          element: (
            <PermissionRoute perm="event_add">
              <AddEvent />
            </PermissionRoute>
          )
        },
        {
          path: 'event/edit/:id',
          element: (
            <PermissionRoute perm="event_edit">
              <EditEvent />
            </PermissionRoute>
          )
        },
        {
          path: 'ticket/add',
          element: (
            <PermissionRoute perm="ticket_book">
              <TicketBookingPage />
            </PermissionRoute>
          )
        },
        {
          path: 'ticket/scan',
          element: (
            <PermissionRoute perm="ticket_scan">
              <TicketScanPage />
            </PermissionRoute>
          )
        },

        {
          path: 'menus',
          element: (
            <BusinessTypeRoute module="menus">
              <PermissionRoute perm="menu_listing">
                <MenusPage />
              </PermissionRoute>
            </BusinessTypeRoute>
          )
        },
        {
          path: 'loyalty',
          element: (
            <PermissionRoute perm="loyalty_access">
              <Loyalty />
            </PermissionRoute>
          )
        },
        {
          path: 'pos-setup',
          element: (
            <BusinessTypeRoute module="pos">
              <PermissionRoute perm="pos_setup">
                <PosSetupPage />
              </PermissionRoute>
            </BusinessTypeRoute>
          )
        },
        {
          path: 'menus/add',
          element: (
            <BusinessTypeRoute module="menus">
              <PermissionRoute perm="menu_add">
                <AddMenuPage />
              </PermissionRoute>
            </BusinessTypeRoute>
          )
        },
        {
          path: 'menus/edit/:id',
          element: (
            <BusinessTypeRoute module="menus">
              <PermissionRoute perm="menu_edit">
                <EditMenuPage />
              </PermissionRoute>
            </BusinessTypeRoute>
          )
        },

        // 404 inside protected area
        { path: '*', element: <NotFoundPage /> }
      ]
    },
    {
      // Full-screen working surface, deliberately outside DashboardLayout —
      // its own topbar, no drawer. A store keeper is not one mis-tap from the
      // booking calendar.
      path: 'store-room',
      element: (
        <ProtectedRoute>
          <PermissionRoute perm="store_room_access">
            <StoreRoom />
          </PermissionRoute>
        </ProtectedRoute>
      )
    },
    {
      // Mobile → web sign-in handoff. Deliberately NOT wrapped in PublicRoute:
      // that redirects away when a token already exists, which would block a
      // handoff meant to switch/refresh the session.
      path: 'auth/handoff',
      element: <HandoffPage />
    },
    {
      path: 'gift-card/view/:code',
      element: <PublicGiftCardView />
    },
    {
      // DLT-approved SMS links arrive as /gift-card/view?CODE — the page redirects to /gift-card/view/CODE.
      path: 'gift-card/view',
      element: <PublicGiftCardView />
    },
    {
      path: 'pos-invoice/:token',
      element: <PublicPosInvoiceView />
    },
    {
      path: 'voucher/validate/:slug',
      element: <PublicVoucherWrapper />
    },
    {
      path: 'voucher/pass/:token',
      element: <PublicVoucherPassPage />
    },
    {
      path: 'voucher/:slug',
      element: <PublicVoucherClaimWrapper />
    },
    // Stripe Checkout result pages — must come before :restaurantId catch-all
    {
      path: 'booking-success',
      element: <BookingSuccess />
    },
    {
      path: 'booking-cancel',
      element: <BookingCancel />
    },
    {
      path: 'booking/confirmation/:bookingRef',
      element: <BookingConfirmationPage />
    },
    // Public ticket booking — must come before the :restaurantId catch-all
    {
      path: 'tickets/:slug',
      element: <PublicTicketWrapper />
    },
    {
      path: 'tickets/:slug/event/:eventId',
      element: <PublicTicketEventDetail />
    },
    {
      path: 'legal/:type',
      element: <LegalPage />
    },
    {
      path: 'payment/result/:txnid',
      element: <PaymentResult />
    },
    {
      path: ':restaurantId',
      element: <PublicBooking />
    },
    {
      path: 'gift/:restaurantId',
      element: <PublicGiftCardUserContainer />
    }
  ]
};

export default PagesRoutes;
